<script setup lang="ts">
import {ConsumerProductIssue} from "@/consumer/interfaces";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Label} from "@/core/components/ui/label";
import {Input} from "@/core/components/ui/input";
import {computed, ref} from "vue";
import {consumerProductApi} from "@/container";

const props = defineProps<{
    consumerProductId: number;
    type: ConsumerProductIssue['type'];
    label: string;
}>();

let commentTimer: ReturnType<typeof setTimeout> | undefined;
const issue = defineModel<ConsumerProductIssue | undefined>("issue");

const commentDraft = ref(issue.value?.comment ?? '');
const isPending = ref(false);

const isChecked = computed(() => !!issue.value);

/**
 * Toggle checkbox issue.
 */
async function toggleIssue(checked: boolean) {
    if (isPending.value) return;
    isPending.value = true;
    if (checked) {
        const previous = issue.value;
        issue.value = {id: -1, type: props.type, comment: commentDraft.value || null};
        try {
            issue.value = await consumerProductApi().createIssue(props.consumerProductId, props.type, commentDraft.value || null);
        } catch {
            issue.value = previous;
        }
    } else {
        const previous = issue.value;
        if (!previous) {
            isPending.value = false;
            return;
        }

        issue.value = undefined;
        commentDraft.value = '';
        try {
            await consumerProductApi().deleteIssue(props.consumerProductId, previous.id);
        } catch {
            issue.value = previous;
            commentDraft.value = previous.comment ?? '';
        }
    }
    isPending.value = false;
}

/**
 * Handle comment input with debounce to avoid too many API calls.
 */
async function onCommentInput(value: string) {
    commentDraft.value = value;
    if (!issue.value) {
        return;
    }

    const issueId = issue.value.id;
    clearTimeout(commentTimer);
    commentTimer = setTimeout(async () => {
        issue.value = await consumerProductApi().updateIssue(props.consumerProductId, issueId, value || null);
    }, 500);
}
</script>

<template>
  <div class="flex items-center gap-2">
    <Checkbox
      :id="consumerProductId + '_' + type"
      :model-value="isChecked"
      :disabled="isPending"
      @update:model-value="toggleIssue($event as boolean)"
    />
    <Label :for="consumerProductId + '_' + type" class="shrink-0">{{ label }}</Label>
    <Input
      placeholder="écrivez un commentaire..."
      :disabled="!isChecked"
      :model-value="commentDraft"
      @input="onCommentInput(($event.target as HTMLInputElement).value)"
    />
  </div>
</template>
